import { useState, useCallback } from 'react';
import * as db from '../lib/supabase-service';
import { User } from '../lib/supabase-service';

export interface Message {
  role: 'user' | 'assistant';
  content: string;
  images?: string[];
  engine?: string;
  timestamp?: string;
}

/**
 * useAIChat Hook v9.1.1 — RAG SIMPLE & EXACTO (Data-Centric)
 * 
 * Misión: "Buscar datos reales primero + Generar respuesta después"
 */
export function useAIChat(user: User | null) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const sendMessage = useCallback(async (content: string, _images?: string[]) => {
    if (!content.trim() || !user) return;

    setIsLoading(true);
    const newUserMessage: Message = { role: 'user', content, timestamp: new Date().toLocaleTimeString() };
    setMessages(prev => [...prev, newUserMessage]);

    let relevantData = "";
    const hasFullAccess =
      user.role === 'SUPER_ADMIN' ||
      user.role === 'ADMINISTRADOR' ||
      user.role === 'DIRECTOR_NACIONAL';

    try {
        // PASO 1 – Buscar datos reales primero (Retrieval)
        let scopeDescription = "";

        // Determinamos el ámbito
        const scope = hasFullAccess ? 'GLOBAL' : (user.org_scope || (user.role === 'COORDINADOR' ? 'COORDINATOR' : user.role === 'VICE_PRESIDENT' ? 'VP' : 'INSTANCE'));

        if (scope === 'GLOBAL') {
            scopeDescription = "Director General / Administrador - Acceso total al sistema nacional.";
        } else if (scope === 'VP') {
            scopeDescription = `Vicepresidente - Acceso a las Comisiones de su Vicepresidencia Nacional.`;
        } else if (scope === 'COORDINATOR') {
            scopeDescription = `Coordinador - Acceso a múltiples instancias autorizadas según su perfil.`;
        } else {
            scopeDescription = "Líder de Instancia - Acceso restringido exclusivamente a su propia Comisión/Comité.";
        }

        // Recuperación
        // Si tiene acceso ampliado por rol real, forzamos consulta global sin depender de identidad textual.
        const queryUser = hasFullAccess ? { ...user, role: 'SUPER_ADMIN' as any, org_scope: null } : user;
        const rawDashboard = await db.getDashboardByCommission(queryUser);
        
        // Simplificamos los datos radicalmente para ahorrar tokens y evitar 429
        const dataSnippet = rawDashboard.map(d => ({
            c: d.commission_name,
            t: d.commission_type === 'ESTRATEGICO' ? 'EST' : (d.commission_type === 'TRABAJO' ? 'TRA' : 'IND'),
            p: d.plan_status,
            g: d.total_goal,
            a: d.total_progress,
            pct: Math.round(d.pct_cumplimiento)
        }));

        // Limitamos el JSON a modo lineal, sin saltos de línea ni tabs
        relevantData = JSON.stringify(dataSnippet);

        // PASO 2 – Generar respuesta después (Generation)
        const sysMsg = hasFullAccess ? `Soy el asistente Gobernanza COPARMEX.
Usuario con ACCESO TOTAL. Responde usando estos datos reales. (c=nombre comisión/comité, t=tipo(EST, TRA, IND), p=estado plan, g=metas, a=avance, pct=%).
DATOS: ${relevantData}` 
        : `Soy el asistente Gobernanza COPARMEX. Tienes ámbito: ${scope}.
DATOS: ${relevantData}`;

        const fallbackModels = ["groq-core"];

        let result: any = null;
        let finalEngine = "";
        let attemptError = "";

        for (const _model of fallbackModels) {
            const controller = new AbortController();
            const id = setTimeout(() => controller.abort(), 22000); // 22 segs por modelo máx

            try {
                const response = await fetch("/api/groq-core", {
                    method: 'POST',
                    signal: controller.signal,
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        prompt: `${sysMsg}\n\nUsuario: ${content}`
                    })
                });

                clearTimeout(id);

                if (response.ok) {
                    result = await response.json();
                    finalEngine = result.engine || _model;
                    break; // Exit loop on success
                } else {
                    const errData = await response.json().catch(() => ({}));
                    attemptError = errData?.error?.message || errData?.error || `HTTP ${response.status}`;
                    console.warn(`[AI Engine] Fallback: ${_model} failed. (${response.status})`);
                }
            } catch (fetchErr: any) {
                clearTimeout(id);
                console.warn(`[AI Engine] Fallback: ${_model} timed out or network error.`);
                attemptError = fetchErr.message;
            }
        }

        if (!result) throw new Error(`Exhausted all fallback AI models. Last error: ${attemptError}`);
        if (result.response && !result.choices) {
            result.choices = [{ message: { content: result.response } }];
        }

        const aiMsg = result.choices?.[0]?.message?.content || "No tengo datos disponibles dentro de tu ámbito de acceso actual.";

        setMessages(prev => [...prev, { 
            role: 'assistant', 
            content: aiMsg,
            engine: finalEngine,
            timestamp: new Date().toLocaleTimeString()
        }]);

    } catch (err: any) {
        console.error("RAG Failure:", err);
        setMessages(prev => [...prev, { 
            role: 'assistant', 
            content: `Error de sistema temporal (OpenRouter Rate Limits): ${err.message}. \n\nDatos de tu acceso:\nTienes Full Access? ${hasFullAccess}. \nTamaño de Datos (Payload RAG): ${relevantData?.length || 0} caracteres.`,
            engine: "Security Guard"
        }]);
    } finally {
        setIsLoading(false);
    }
  }, [user]);

  const clearChat = useCallback(() => setMessages([]), []);

  return { 
    messages, 
    sendMessage, 
    isLoading, 
    clearChat, 
    remainingQueries: 999
  };
}
